import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from './components/Navbar';
import ConsentBanner from './components/ConsentBanner';
import './App.css';
import { EasterEggProvider } from './context/EasterEggContext';
import EasterEggEffect from './components/EasterEggEffect';
import Background3D from './components/Background3D';
import CursorTrail from './components/CursorTrail';
import { getFingerprint } from './utils/fingerprint';
import { useDiscordAuth } from './hooks/useDiscordAuth';

import logo from './tansicon.png';

// Lazy loaded pages
const Storefront = lazy(() => import('./pages/Storefront'));
const Credits = lazy(() => import('./pages/Credits'));
const MemberHub = lazy(() => import('./pages/MemberHub'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const About = lazy(() => import('./pages/About'));
const NotFound = lazy(() => import('./pages/NotFound'));

const MAINTENANCE_MODE = false;

const FUN_FACTS = [
  "Honey never spoils. Archaeologists have found edible honey in ancient Egyptian tombs.",
  "The longest word in the English language is 'supercalifragilisticexpialidocious'.",
  "A group of koalas is called a 'koala pod'.",
  "I did not get paid for this Website.",
  "A day on Venus is longer than a year on Venus.",
  "Octopuses have three hearts and blue blood.",
  "Bananas are berries, but strawberries aren't.",
  "There are more trees on Earth than stars in the Milky Way.",
  "The Eiffel Tower can be 15 cm taller during the summer due to thermal expansion.",
  "A group of flamingos is called a 'flamboyance'.",
  "The shortest war in history lasted 38 minutes between Britain and Zanzibar.",
  "Wombat poop is cube-shaped to prevent it from rolling away.",
  "The heart of a shrimp is located in its head.",
  "Sloths can hold their breath longer than dolphins can.",
  "An individual blood cell takes about 60 seconds to make a complete circuit of the body.",
  "The first oranges weren't orange—they were green.",
  "A snail can sleep for three years.",
  "The fingerprints of koalas are so indistinguishable from humans' that they have on occasion been confused at crime scenes.",
  "There are no muscles in your fingers: Their function is controlled by muscles in your palms and arms.",
  "Scotland has 421 words for 'snow'.",
  "The 'm's' in M&Ms stand for Mars and Murrie.",
  "A cloud can weigh more than a million pounds.",
  "The Twitter bird's official name is Larry.",
  "The inventor of the Pringles can is buried in one.",
  "It is impossible to hum while holding your nose.",
  "Rabbits can't vomit.",
  "A jiffy is an actual unit of time: 1/100th of a second.",
  "The moon has moonquakes."
];

const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#fff' }}>
    <h2>Loading...</h2>
  </div>
);

const AppContent = () => {
  const {
    discordUser,
    isMember,
    hasCustomerRole,
    isAdmin,
    isConnecting,
    handleConnectDiscord,
    handleLogout
  } = useDiscordAuth();
  
  const appRef = useRef(null);
  const [factIndex, setFactIndex] = useState(0);

  // Visitor Logging / Fingerprinting
  useEffect(() => {
    if (MAINTENANCE_MODE) return;
    
    const logVisitor = async () => {
      try {
        const hasLogged = sessionStorage.getItem('shocz_v_log');
        if (hasLogged) return;

        const fingerprint = await getFingerprint();
        await fetch('/.netlify/functions/log-visitor', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fingerprint)
        });
        
        sessionStorage.setItem('shocz_v_log', 'true');
      } catch (e) {
        console.error('Logging failed:', e);
      }
    };

    // Slight delay to not block initial render
    const timer = setTimeout(logVisitor, 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    document.title = MAINTENANCE_MODE ? "Maintenance | Shocz Tweaks" : "Shocz Tweaks | Premium PC Optimization";
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setFactIndex((prev) => (prev + 1) % FUN_FACTS.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);



  return (
    <div className="app" ref={appRef}>
      {MAINTENANCE_MODE ? (
        <div className="maintenance-screen">
          <div className="maintenance-logo-container">
            <img src={logo} alt="Logo" className="maintenance-logo" />
          </div>
          <h1 className="maintenance-text">We'll be right back</h1>
          <div className="maintenance-funfact-container">
            <p className="funfact-label">Did you know?</p>
            <p className="funfact-text" key={factIndex}>{FUN_FACTS[factIndex]}</p>
          </div>
        </div>
      ) : (
        <>
          <Background3D />
          <EasterEggEffect />
          <ToastContainer position="bottom-right" theme="dark" />
          <ConsentBanner />
          <Navbar discordUser={discordUser} onLogout={handleLogout} onConnect={handleConnectDiscord} />
          
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route 
                path="/" 
                element={
                  <Storefront 
                    discordUser={discordUser} 
                    isMember={isMember}
                    isAdmin={isAdmin}
                    isConnecting={isConnecting} 
                    onConnect={handleConnectDiscord} 
                    onLogout={handleLogout} 
                  />
                } 
              />
              <Route path="/credits" element={<Credits />} />
              <Route path="/about" element={<About />} />
              <Route 
                path="/hub" 
                element={
                  <MemberHub 
                    discordUser={discordUser} 
                    isMember={isMember}
                    hasCustomerRole={hasCustomerRole}
                    isAdmin={isAdmin}
                    onConnect={handleConnectDiscord} 
                    onLogout={handleLogout} 
                  />
                } 
              />
              <Route 
                path="/admin" 
                element={
                  <AdminDashboard 
                    discordUser={discordUser} 
                    isAdmin={isAdmin} 
                  />
                } 
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </>
      )}
    </div>
  );
};

function App() {
  return (
    <EasterEggProvider>
      <Router>
        <AppContent />
      </Router>
    </EasterEggProvider>
  );
}

export default App;